const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0", "api", "saleor"]);

/**
 * Origin embedded in HTML/CSS for browsers and for `next/image` `src` / preload hints.
 *
 * Never use Docker-only hostnames like `http://api:8000` here — the browser cannot resolve `api`.
 * For local compose, set `NEXT_PUBLIC_SALEOR_IMAGE_ORIGIN=http://localhost:8000` (or your public API host).
 *
 * `SALEOR_IMAGE_ORIGIN` is for server-to-Saleor HTTP (GraphQL, scripts), not for media URLs in the DOM.
 */
function getNormalizedOrigin(): string {
	return process.env.NEXT_PUBLIC_SALEOR_IMAGE_ORIGIN || "http://localhost:8000";
}

/**
 * Normalize Saleor media URLs for use in the storefront.
 * - Relative URLs (/media/...) → prepend configured origin
 * - Local/dev origins (localhost, api, saleor in Docker) → replace with configured origin
 * - Saleor Cloud (api.saleor.cloud, *.saleor.cloud) → pass through unchanged
 */
export function normalizeSaleorMediaUrl(url: string | null | undefined): string {
	if (!url) return "";
	if (url.startsWith("//")) {
		return `https:${url}`;
	}

	if (url.startsWith("/")) {
		return `${getNormalizedOrigin()}${url}`;
	}

	try {
		const parsed = new URL(url);
		const hostname = parsed.hostname.toLowerCase();
		// Only rewrite if hostname is exactly a local/dev host (not api.saleor.cloud etc.)
		if (LOCAL_HOSTNAMES.has(hostname)) {
			const origin = getNormalizedOrigin().replace(/\/$/, "");
			const path = parsed.pathname + parsed.search;
			return `${origin}${path}`;
		}
		// Production media must never load over http on an https page: Saleor
		// builds absolute media URLs with http:// when ENABLE_SSL is off.
		if (parsed.protocol === "http:") {
			parsed.protocol = "https:";
			return parsed.toString();
		}
	} catch {
		// Invalid URL, return as-is
	}

	return url;
}
